/**
 * Device audio — a bonus, never at the expense of video or control.
 *
 * It arrives as **Opus over a WebSocket, one packet per message** (Opus packets are not
 * self-delimiting, so a byte stream cannot be split back into packets at all — that is why the
 * earlier chunked-HTTP endpoint was unusable). Opus and not raw PCM because PCM measured
 * 1529 kbit/s and the build machines are on LTE: ~64 kbit/s with sound, ~1–6 kbit/s in silence.
 * The first message is the codec config (OpusHead).
 *
 * Hard-won details, all of which produced silence-with-no-error at some point:
 *
 * - **Probe the decoder config, never assume.** Chrome rejects `description`/OpusHead outright
 *   ("Unsupported configuration") and decodes raw Opus packets happily without it. Try variants
 *   through `isConfigSupported()`.
 * - **Keep ~150 ms of scheduling lead.** Re-arming with 20 ms is thinner than ordinary jitter, so
 *   every buffer started "now", overlapping the previous one — mush, or nothing.
 * - **De-interleave by hand.** Asking `copyTo` for a conversion the browser does not support throws
 *   inside the output callback, where it is invisible without a catch.
 * - **A quiet phone is not a broken stream.** Opus encodes silence to ~3-byte packets; reporting
 *   that as failure sends people looking for a bug that is not there.
 * - **Relay the server's own reason** when audio is unavailable. The old viewer translated "no
 *   audio" into "needs Android 11+" and said that about an Android 16 Pixel whose real problem was
 *   the hub dropping the audio flag.
 */

import type { TikTokHubApi } from '../api/client'
import type { Dict } from '../i18n/en'

export interface AudioStat {
  /** human-readable wire state: no packets / quiet / N kbit/s */
  wire: string
  decodedPerSec: number
  ctxState: string
  cfg: string | null
  error: string | null
}

export interface AudioStreamOptions {
  api: TikTokHubApi
  sessionId: string
  /** Phrases come from the caller: this module reports state to a human, in their language. */
  t: Dict
  /** 0..2, 1 = as sent by the phone */
  gain?: number
  onStat?: (stat: AudioStat) => void
  onUnavailable?: (hint: string) => void
  onReady?: () => void
}

const PACKET_MS = 20
const LEAD_S = 0.15

export class AudioStream {
  private ctx: AudioContext | null = null
  private gainNode: GainNode | null = null
  private ws: WebSocket | null = null
  private decoder: AudioDecoder | null = null
  private configuring = false
  private statTimer: ReturnType<typeof setInterval> | null = null
  private disposed = false

  private bytes = 0
  private packets = 0
  private decoded = 0
  private cfg: string | null = null
  private error: string | null = null
  private next = 0
  private ts = 0
  private startedAt = 0

  constructor(private readonly o: AudioStreamOptions) {}

  get running(): boolean {
    return this.ws !== null && !this.disposed
  }

  async start(): Promise<void> {
    if (this.disposed) return
    let ctx: AudioContext
    try {
      ctx = new AudioContext({ sampleRate: 48000 })
      await ctx.resume()
    } catch {
      this.o.onUnavailable?.(this.o.t.audio.noContext)
      return
    }
    this.ctx = ctx
    // The phone's own volume (Vol± keys) and how loud this phone should be in the listener's ears
    // are separate problems — especially with several devices open.
    const gain = ctx.createGain()
    gain.gain.value = this.o.gain ?? 1
    gain.connect(ctx.destination)
    this.gainNode = gain
    this.next = ctx.currentTime + 0.12

    this.startedAt = Date.now()
    this.statTimer = setInterval(() => this.reportStat(), 1000)

    const ws = new WebSocket(this.o.api.wsUrl(`/v1/sessions/${this.o.sessionId}/audio-ws`))
    ws.binaryType = 'arraybuffer'
    this.ws = ws
    ws.onmessage = (ev) => void this.onMessage(ev)
    ws.onclose = () => {
      if (this.ws === ws) this.stop()
    }
    ws.onerror = () => {
      if (this.ws === ws) this.stop()
    }
  }

  stop(): void {
    this.disposed = true
    if (this.statTimer) {
      clearInterval(this.statTimer)
      this.statTimer = null
    }
    const ws = this.ws
    this.ws = null
    if (ws) {
      try {
        ws.close()
      } catch {
        /* already closing */
      }
    }
    if (this.decoder) {
      try {
        this.decoder.close()
      } catch {
        /* already closed */
      }
      this.decoder = null
    }
    if (this.ctx) {
      try {
        void this.ctx.close()
      } catch {
        /* already closed */
      }
      this.ctx = null
    }
    this.gainNode = null
  }

  setGain(value: number): void {
    if (this.gainNode) this.gainNode.gain.value = value
  }

  /**
   * 440 Hz straight into the output — a check on whether this browser, on this machine, plays
   * anything at all.
   *
   * It connects to `ctx.destination` **directly, never through our gain node**. A diagnostic must
   * not run through the chain it is diagnosing: routing it through our gain made a silent tone mean
   * two different things at once ("our chain is muted" and "the machine is playing to another
   * device"), and it cost a round of debugging to tell them apart. It reports our chain's gain
   * alongside, so the two can be read off one line.
   */
  async testTone(): Promise<string> {
    const ctx = this.ctx ?? new AudioContext({ sampleRate: 48000 })
    this.ctx = ctx
    await ctx.resume().catch(() => {})
    const osc = ctx.createOscillator()
    const g = ctx.createGain()
    osc.frequency.value = 440
    g.gain.value = 0.25
    osc.connect(g)
    g.connect(ctx.destination)
    osc.start()
    osc.stop(ctx.currentTime + 0.4)
    const chain = this.gainNode
      ? this.o.t.audio.chainGain(this.gainNode.gain.value.toFixed(2))
      : this.o.t.audio.chainClosed
    return this.o.t.audio.toneReport(ctx.state, ctx.destination.maxChannelCount, chain)
  }

  private reportStat(): void {
    const ctx = this.ctx
    if (!ctx) return
    // An autoplay-blocked context stays "suspended" and silently swallows everything; a click
    // anywhere may have unlocked it since, so keep nudging.
    if (ctx.state !== 'running') void ctx.resume().catch(() => {})
    const kbit = (this.bytes * 8) / 1000
    // The hub starts its audio process only when asked, and that takes about four seconds. Calling
    // that "no packets" would read as a failure during the normal case.
    const booting = Date.now() - this.startedAt < 7000
    const wire = !this.packets
      ? booting
        ? this.o.t.audio.starting
        : this.o.t.audio.noPackets
      : this.bytes / this.packets <= 10
        ? this.o.t.audio.quiet
        : this.o.t.audio.kbit(kbit.toFixed(0))
    if (this.decoded > 0) this.error = null // decoding again → the old error is stale
    this.o.onStat?.({
      wire,
      decodedPerSec: this.decoded,
      ctxState: ctx.state,
      cfg: this.cfg,
      error: this.error,
    })
    this.bytes = 0
    this.packets = 0
    this.decoded = 0
  }

  private async onMessage(ev: MessageEvent): Promise<void> {
    if (this.disposed) return
    // A text frame is the server declining, carrying the hub's own reason.
    if (typeof ev.data === 'string') {
      let hint = this.o.t.audio.unavailable
      try {
        hint = (JSON.parse(ev.data) as { hint?: string }).hint ?? hint
      } catch {
        /* not JSON — keep the default */
      }
      this.o.onUnavailable?.(hint)
      this.stop()
      return
    }
    const bytes = new Uint8Array(ev.data as ArrayBuffer)
    this.bytes += bytes.length
    this.packets++

    if (this.configuring) return // dropping a few 20 ms packets beats re-entering setup
    if (!this.decoder) {
      this.configuring = true
      const dec = new AudioDecoder({
        output: (data) => this.playOut(data),
        error: (e) => {
          this.error = this.o.t.audio.decoderError(e instanceof Error ? e.message : String(e))
        },
      })
      const variants: AudioDecoderConfig[] = [
        { codec: 'opus', sampleRate: 48000, numberOfChannels: 2 }, // raw packets
        { codec: 'opus', sampleRate: 48000, numberOfChannels: 2, description: bytes }, // OpusHead
      ]
      let used: AudioDecoderConfig | null = null
      for (const cfg of variants) {
        try {
          const probe = await AudioDecoder.isConfigSupported(cfg)
          if (probe?.supported) {
            dec.configure(cfg)
            used = cfg
            break
          }
        } catch {
          /* unsupported variant → try the next */
        }
      }
      this.configuring = false
      if (!used) {
        this.error = this.o.t.audio.noOpus
        return
      }
      this.cfg = used.description ? 'з OpusHead' : 'raw'
      this.decoder = dec
      this.o.onReady?.()
      return
    }
    if (this.decoder.state !== 'configured') return
    this.decoder.decode(new EncodedAudioChunk({ type: 'key', timestamp: this.ts, data: bytes }))
    this.ts += PACKET_MS * 1000 // µs
  }

  private playOut(data: AudioData): void {
    this.decoded++
    const ctx = this.ctx
    const gain = this.gainNode
    if (!ctx || !gain) {
      data.close()
      return
    }
    try {
      const chans = data.numberOfChannels
      const frames = data.numberOfFrames
      const buffer = ctx.createBuffer(chans, frames, data.sampleRate)
      if ((data.format ?? '').endsWith('-planar')) {
        for (let ch = 0; ch < chans; ch++) {
          const tmp = new Float32Array(frames)
          data.copyTo(tmp, { planeIndex: ch, format: 'f32-planar' })
          buffer.getChannelData(ch).set(tmp)
        }
      } else {
        const inter = new Float32Array(frames * chans)
        data.copyTo(inter, { planeIndex: 0, format: 'f32' })
        for (let ch = 0; ch < chans; ch++) {
          const out = buffer.getChannelData(ch)
          for (let i = 0; i < frames; i++) out[i] = inter[i * chans + ch]!
        }
      }
      data.close()
      const src = ctx.createBufferSource()
      src.buffer = buffer
      src.connect(gain)
      if (this.next < ctx.currentTime + 0.02) this.next = ctx.currentTime + LEAD_S
      src.start(this.next)
      this.next += buffer.duration
    } catch (e) {
      this.error = this.o.t.audio.outputError(e instanceof Error ? e.message : String(e))
    }
  }
}
